import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import { Crown, Zap, AlertCircle, Loader2, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react'
import { SubscriptionTypeEnum } from '../../lib/enums'
import { cn } from '../../lib/utils'

interface UpgradeDowngradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTier: SubscriptionTypeEnum
  targetTier: SubscriptionTypeEnum
  onConfirm: () => Promise<void>
  loading?: boolean
}

interface TierInfo {
  name: string
  price: string
  priceMonthly: number
  icon: React.ReactNode
  color: string
  features: string[]
}

const tierInfo: Record<SubscriptionTypeEnum, TierInfo> = {
  [SubscriptionTypeEnum.Free]: {
    name: 'Free (No Subscription)',
    price: '$0.00',
    priceMonthly: 0,
    icon: <Zap className="w-5 h-5" />,
    color: 'text-neutral-500 dark:text-neutral-400',
    features: [
      'No features available',
      'Choose a subscription to get started',
    ],
  },
  [SubscriptionTypeEnum.Basic]: {
    name: 'Basic',
    price: '$9.99',
    priceMonthly: 9.99,
    icon: <Zap className="w-5 h-5" />,
    color: 'text-neutral-700 dark:text-neutral-300',
    features: [
      'Up to 200 inventory items',
      'Inventory tracking',
      'Listing management',
      'Sales tracking',
      'Earnings dashboard',
      'Units & bins organization',
      'Manual barcode entry',
      'Community support',
    ],
  },
  [SubscriptionTypeEnum.Premium]: {
    name: 'Premium',
    price: '$19.99',
    priceMonthly: 19.99,
    icon: <Crown className="w-5 h-5" />,
    color: 'text-sumoPrimary',
    features: [
      'Everything in Basic',
      'Up to 1000 inventory items',
      'Barcode generation & printing',
      'Barcode scanning (coming soon)',
      'Export inventory CSV',
      'Advanced reporting',
      'Priority support',
      'Enhanced analytics',
    ],
  },
  [SubscriptionTypeEnum.Enterprise]: {
    name: 'Enterprise',
    price: '$49.99',
    priceMonthly: 49.99,
    icon: <Crown className="w-5 h-5" />,
    color: 'text-sumoAmber',
    features: [
      'Everything in Premium',
      'Unlimited inventory items',
      'Multi-user access',
      'API access',
      'Custom integrations',
      'Additional stores ($5/store)',
      'Custom CSV reporting',
      'Dedicated support',
      'Custom training',
    ],
  },
}

const UpgradeDowngradeDialog: FC<UpgradeDowngradeDialogProps> = ({
  open,
  onOpenChange,
  currentTier,
  targetTier,
  onConfirm,
  loading = false,
}) => {
  const [confirming, setConfirming] = useState(false)

  const current = tierInfo[currentTier]
  const target = tierInfo[targetTier]

  const isUpgrade = target.priceMonthly > current.priceMonthly
  const priceDifference = Math.abs(target.priceMonthly - current.priceMonthly)

  const handleConfirm = async () => {
    setConfirming(true)
    try {
      await onConfirm()
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isUpgrade ? (
              <>
                <TrendingUp className="w-5 h-5 text-success500" />
                Upgrade Your Plan
              </>
            ) : (
              <>
                <TrendingDown className="w-5 h-5 text-warning500" />
                Downgrade Your Plan
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isUpgrade
              ? 'Unlock more features and grow your business'
              : 'Reduce your subscription cost'}
          </DialogDescription>
        </DialogHeader>

        {/* Current vs Target Comparison */}
        <div className="grid grid-cols-2 gap-4 my-6">
          {/* Current Plan */}
          <div className="p-4 border-2 border-neutral-200 dark:border-neutral-800 rounded-lg bg-neutral-50 dark:bg-neutral-900">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-neutral-200 dark:bg-neutral-800">
                {current.icon}
              </div>
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Current</p>
                <h3 className="font-bold text-neutral-900 dark:text-neutral-100">
                  {current.name}
                </h3>
              </div>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-3">
              {current.price}
              <span className="text-sm font-normal text-neutral-600 dark:text-neutral-400">/month</span>
            </p>
          </div>

          {/* Target Plan */}
          <div className={cn(
            'p-4 border-2 rounded-lg relative',
            isUpgrade
              ? 'border-sumoPrimary bg-sumoPrimary/5 dark:bg-sumoPrimary/30'
              : 'border-warning500 bg-warning500/5 dark:bg-warning500/30'
          )}>
            <Badge
              className={cn(
                'absolute -top-3 left-1/2 -translate-x-1/2',
                isUpgrade
                  ? 'bg-sumoPrimary text-sumoNavy'
                  : 'bg-warning500 text-neutral-900'
              )}
            >
              {isUpgrade ? 'Upgrading to' : 'Downgrading to'}
            </Badge>
            <div className="flex items-center gap-2 mb-2">
              <div className={cn(
                'p-2 rounded-full',
                isUpgrade
                  ? 'bg-sumoPrimary/10 dark:bg-sumoPrimary/30'
                  : 'bg-warning500/10 dark:bg-warning500/30'
              )}>
                <div className={target.color}>
                  {target.icon}
                </div>
              </div>
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">New Plan</p>
                <h3 className="font-bold text-neutral-900 dark:text-neutral-100">
                  {target.name}
                </h3>
              </div>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-3">
              {target.price}
              <span className="text-sm font-normal text-neutral-600 dark:text-neutral-400">/month</span>
            </p>
          </div>
        </div>

        {/* Price Difference */}
        <Alert className={cn(
          'border-2',
          isUpgrade
            ? 'bg-sumoPrimary/5 dark:bg-sumoPrimary/20 border-sumoPrimary text-neutral-900 dark:text-neutral-100'
            : 'bg-success500/5 dark:bg-success500/20 border-success500 text-neutral-900 dark:text-neutral-100'
        )}>
          <ArrowRight className={cn(
            'h-4 w-4',
            isUpgrade
              ? 'text-sumoPrimary'
              : 'text-success500'
          )} />
          <AlertDescription>
            <div className="font-medium text-neutral-900 dark:text-neutral-100">
              {isUpgrade ? (
                <>
                  Your new monthly cost will be{' '}
                  <span className="text-sumoPrimary font-bold">{target.price}</span>
                  {' '}(+${priceDifference.toFixed(2)}/month)
                </>
              ) : (
                <>
                  You'll save{' '}
                  <span className="text-success500 font-bold">${priceDifference.toFixed(2)}/month</span>
                  {' '}with your new plan
                </>
              )}
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Your first payment will be prorated based on your current billing cycle.
            </p>
          </AlertDescription>
        </Alert>

        {/* Features Gained/Lost */}
        <div className="mt-4">
          <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
            {isUpgrade ? "What you'll gain:" : "Important changes:"}
          </h4>
          <ul className="space-y-2">
            {target.features.slice(0, 5).map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <div className={cn(
                  'mt-0.5 rounded-full p-0.5',
                  isUpgrade
                    ? 'bg-success500/10 dark:bg-success500/30'
                    : 'bg-warning500/10 dark:bg-warning500/30'
                )}>
                  {isUpgrade ? (
                    <TrendingUp className="w-3 h-3 text-success500" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-warning500" />
                  )}
                </div>
                <span className="text-neutral-700 dark:text-neutral-300">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Warning for Downgrades */}
        {!isUpgrade && (
          <Alert variant="warning" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Downgrade Considerations</p>
              <p className="text-sm mt-1">
                Some features will be removed from your account. Your data will be preserved,
                but you may lose access to certain functionality.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Footer */}
        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirming || loading}
          >
            Cancel
          </Button>
          <Button
            variant={isUpgrade ? 'primary' : 'outline'}
            onClick={handleConfirm}
            disabled={confirming || loading}
          >
            {confirming || loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {isUpgrade ? 'Confirm Upgrade' : 'Confirm Downgrade'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpgradeDowngradeDialog
